import Token from 'common/models/MyToken';
import { Nullable } from 'common/models/optional';
import {
  FARMING_V2,
  ICHI_NEW_TOKENS,
  XICHI_TOKENS,
  USDC_TOKENS,
  DAI_TOKENS,
  ONEINCH_TOKENS,
  ONE_1INCH_TOKENS,
  WBTC_TOKENS,
  ONE_BTC_TOKENS,
  ONE_FIL_TOKENS,
  FUSE_TOKENS,
  ONE_FUSE_TOKENS,
  GIV_TOKENS,
  ONE_GIV_TOKENS,
  ONE_MPH_TOKENS,
  ONE_OJA_TOKENS,
  ONE_PERL_TOKENS,
  ONE_ICHI_TOKENS,
  ONE_DODO_TOKENS,
  FOX_TOKENS,
  ONE_FOX_TOKENS,
  ONE_WING_TOKENS,
  BOOT_USD_TOKENS,
  ONE_UNI_TOKENS,
  OTI_TOKENS,
  ChainId,
  ICHI_LEGACY_TOKENS,
  Farm,
} from 'constants/constants';
import { Address } from 'viem';
import { oneTokenV1 } from 'constants/abis/oneTokenV1';
import { farmingV2 } from './abis/farmingV2';
import { ichi } from './abis/ichi';
import { ichiV2 } from './abis/ichiV2';
import { ichiStaking } from './abis/ichiStaking';

export function isIchiContract(
  contract: Nullable<Token>,
  args: { chainId: number; container: IchiContract<typeof ichi> }
): boolean {
  return (
    contract &&
    ICHI_NEW_TOKENS[args.chainId] &&
    args.container.token.symbol === ICHI_NEW_TOKENS[args.chainId].symbol
  );
}

export function isIchiV2Contract(
  contract: Nullable<Token>,
  args: { chainId: number; container: IchiContract<typeof ichiV2> }
): boolean {
  return (
    contract &&
    ICHI_NEW_TOKENS[args.chainId] &&
    args.container.token.symbol === ICHI_NEW_TOKENS[args.chainId].symbol
  );
}

export function isIchiStakingContract(
  contract: Nullable<Token>,
  args: { chainId: number; container: IchiContract<typeof ichiStaking> }
): boolean {
  return (
    contract != null &&
    [XICHI_TOKENS[args.chainId]?.symbol]
      .filter((f) => f)
      .includes(args.container.token.symbol)
  );
}

export function isOneTokenV1Contract(
  contract: Nullable<Token>,
  args: { chainId: number; contractContainer: MintableToken }
): boolean {
  return (
    contract != null &&
    [
      ONE_FIL_TOKENS[args.chainId]?.symbol,
      ONE_1INCH_TOKENS[args.chainId]?.symbol,
      OTI_TOKENS[args.chainId]?.symbol,
      ONE_UNI_TOKENS[args.chainId]?.symbol,
      ONE_FUSE_TOKENS[args.chainId]?.symbol,
      ONE_GIV_TOKENS[args.chainId]?.symbol,
      ONE_OJA_TOKENS[args.chainId]?.symbol,
      ONE_PERL_TOKENS[args.chainId]?.symbol,
      ONE_ICHI_TOKENS[args.chainId]?.symbol,
      ONE_DODO_TOKENS[args.chainId]?.symbol,
      ONE_FOX_TOKENS[args.chainId]?.symbol,
      ONE_WING_TOKENS[args.chainId]?.symbol,
      BOOT_USD_TOKENS[args.chainId]?.symbol,
      ONE_MPH_TOKENS[args.chainId]?.symbol,
      ONE_BTC_TOKENS[args.chainId]?.symbol,
    ]
      .filter((f) => f)
      .includes(args.contractContainer.token.symbol)
  );
}

export function isFarmingV2ContractByVersion(
  chainId: number,
  version: number,
  contract: Nullable<Farm>
): boolean {
  return (
    contract != null &&
    [FARMING_V2[chainId]?.version].filter((f) => f).includes(version)
  );
}

export function insufficientTokenMessage(token?: string): string {
  return `Insufficient ${token} amount!`;
}

export type FarmContract = {
  version: number;
  token: Farm;
  abi: typeof farmingV2;
};

type FarmContracts = Record<number, Record<Address, FarmContract>>;

export const FARM_CONTRACTS: FarmContracts = {
  // ---------------------------------------------
  // Mainnet
  // ---------------------------------------------
  // NOTE: Each ContractContainer implicitly has 'contract' on it.
  [ChainId.Mainnet]: {
    // -----------------
    // Farming
    // -----------------
    [FARMING_V2[ChainId.Mainnet].address]: {
      version: FARMING_V2[ChainId.Mainnet].version,
      token: FARMING_V2[ChainId.Mainnet],
      abi: farmingV2,
    },
  },
  // ---------------------------------------------
  // Polygon
  // ---------------------------------------------
  [ChainId.Polygon]: {
    // -----------------
    // Farming
    // -----------------
    [FARMING_V2[ChainId.Polygon].address]: {
      version: FARMING_V2[ChainId.Polygon].version,
      token: FARMING_V2[ChainId.Polygon],
      abi: farmingV2,
    },
  },
  // ---------------------------------------------
  // Mumbai
  // ---------------------------------------------
  [ChainId.Mumbai]: {
    // -----------------
    // Farming
    // -----------------
    [FARMING_V2[ChainId.Mumbai].address]: {
      version: FARMING_V2[ChainId.Mumbai].version,
      token: FARMING_V2[ChainId.Mumbai],
      abi: farmingV2,
    },
  },
};

export type MintableToken = {
  token: Token;
  memberToken: Token;
  collateralToken: Token;
  memberOracle?: Token;
  abi: typeof oneTokenV1;
};

type MintableTokens = Record<number, Record<Address, MintableToken>>;

export const MINTABLE_TOKENS: MintableTokens = {
  [ChainId.Mainnet]: {
    [ONE_1INCH_TOKENS[ChainId.Mainnet].address]: {
      token: ONE_1INCH_TOKENS[ChainId.Mainnet],
      memberToken: ONEINCH_TOKENS[ChainId.Mainnet],
      collateralToken: USDC_TOKENS[ChainId.Mainnet],
      abi: oneTokenV1,
    },
    [ONE_FUSE_TOKENS[ChainId.Mainnet].address]: {
      token: ONE_FUSE_TOKENS[ChainId.Mainnet],
      memberToken: FUSE_TOKENS[ChainId.Mainnet],
      collateralToken: USDC_TOKENS[ChainId.Mainnet],
      abi: oneTokenV1,
    },
    [ONE_GIV_TOKENS[ChainId.Mainnet].address]: {
      token: ONE_GIV_TOKENS[ChainId.Mainnet],
      memberToken: GIV_TOKENS[ChainId.Mainnet],
      collateralToken: DAI_TOKENS[ChainId.Mainnet],
      abi: oneTokenV1,
    },
    [ONE_ICHI_TOKENS[ChainId.Mainnet].address]: {
      token: ONE_ICHI_TOKENS[ChainId.Mainnet],
      memberToken: ICHI_NEW_TOKENS[ChainId.Mainnet],
      collateralToken: USDC_TOKENS[ChainId.Mainnet],
      abi: oneTokenV1,
    },
    [ONE_FOX_TOKENS[ChainId.Mainnet].address]: {
      token: ONE_FOX_TOKENS[ChainId.Mainnet],
      memberToken: FOX_TOKENS[ChainId.Mainnet],
      collateralToken: USDC_TOKENS[ChainId.Mainnet],
      abi: oneTokenV1,
    },
  },
  [ChainId.Polygon]: {
    [ONE_BTC_TOKENS[ChainId.Polygon].address]: {
      token: ONE_BTC_TOKENS[ChainId.Polygon],
      memberToken: WBTC_TOKENS[ChainId.Polygon],
      collateralToken: USDC_TOKENS[ChainId.Polygon],
      abi: oneTokenV1,
    },
  },
};

export type IchiContract<T> = {
  token: Token;
  abi: T;
};

type IchiTokens<T> = Record<number, Record<Address, IchiContract<T>>>;

export const ICHI_LEGACY_CONTRACTS: IchiTokens<typeof ichi> = {
  [ChainId.Mainnet]: {
    [ICHI_LEGACY_TOKENS[ChainId.Mainnet].address]: {
      token: ICHI_LEGACY_TOKENS[ChainId.Mainnet],
      abi: ichi,
    },
  },
  [ChainId.Goerli]: {
    [ICHI_LEGACY_TOKENS[ChainId.Goerli].address]: {
      token: ICHI_LEGACY_TOKENS[ChainId.Goerli],
      abi: ichi,
    },
  },
};

export const ICHI_CONTRACTS: IchiTokens<typeof ichiV2> = {
  [ChainId.Mainnet]: {
    [ICHI_NEW_TOKENS[ChainId.Mainnet].address]: {
      token: ICHI_NEW_TOKENS[ChainId.Mainnet],
      abi: ichiV2,
    },
  },
  [ChainId.Goerli]: {
    [ICHI_NEW_TOKENS[ChainId.Goerli].address]: {
      token: ICHI_NEW_TOKENS[ChainId.Goerli],
      abi: ichiV2,
    },
  },
  [ChainId.Polygon]: {
    [ICHI_NEW_TOKENS[ChainId.Polygon].address]: {
      token: ICHI_NEW_TOKENS[ChainId.Polygon],
      abi: ichiV2,
    },
  },
  [ChainId.Mumbai]: {
    [ICHI_NEW_TOKENS[ChainId.Mumbai].address]: {
      token: ICHI_NEW_TOKENS[ChainId.Mumbai],
      abi: ichiV2,
    },
  },
};

export const ICHI_STAKING_CONTRACTS: IchiTokens<typeof ichiStaking> = {
  [ChainId.Mainnet]: {
    [XICHI_TOKENS[ChainId.Mainnet].address]: {
      token: XICHI_TOKENS[ChainId.Mainnet],
      abi: ichiStaking,
    },
  },
};
